<script setup lang="ts">
import { computed, ref, type Component } from 'vue';
import { AlertCircle } from 'lucide-vue-next';
import BaseIcon from './BaseIcon.vue';

interface Props {
  modelValue: string | number;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time';
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  icon?: Component;
  iconPosition?: 'left' | 'right';
  id?: string;
  autocomplete?: string;
  inputmode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
  min?: string | number;
  max?: string | number;
  step?: string | number;
  ariaLabel?: string;
}

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  focus: [];
  blur: [];
  change: [];
}>();

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  disabled: false,
  readonly: false,
  required: false,
  iconPosition: 'left',
});

// Generate unique ID if not provided
const inputId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`);

// Track if field has been touched for error display
const isTouched = ref(false);

const hasError = computed(() => !!props.error);

const inputClass = computed(() => {
  const baseClasses = [
    'w-full',
    'px-sm',
    'py-xs',
    'bg-bg-tertiary',
    'border',
    'border-border-light',
    'rounded-md',
    'text-text-primary',
    'transition-all',
    'duration-base',
    'ease-default',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-primary',
    'focus:border-transparent',
    'placeholder:text-text-tertiary',
    'text-base',
  ];

  // Error state styling
  if (hasError.value) {
    baseClasses.push('border-error', 'focus:ring-error');
  }

  // Disabled/readonly state
  if (props.disabled || props.readonly) {
    baseClasses.push('opacity-50', 'cursor-not-allowed', 'bg-bg-secondary');
  }

  // Icon spacing
  if (props.icon) {
    if (props.iconPosition === 'left') {
      baseClasses.push('pl-10');
    } else {
      baseClasses.push('pr-10');
    }
  }

  // Error shake animation
  if (hasError.value && isTouched.value) {
    baseClasses.push('animate-shake-once');
  }

  return baseClasses.join(' ');
});

const wrapperClass = computed(() => {
  const baseClasses = ['relative'];
  return baseClasses.join(' ');
});

const labelClass = computed(() => {
  return [
    'block',
    'text-sm',
    'font-medium',
    'mb-1',
    'text-text-secondary',
  ].join(' ');
});

const errorClass = computed(() => [
  'text-sm',
  'text-error',
  'mt-1',
  'flex',
  'items-center',
  'gap-1',
].join(' '));

const helperClass = computed(() => [
  'text-xs',
  'text-text-tertiary',
  'mt-1',
].join(' '));

const requiredIndicatorClass = computed(() => [
  'text-error',
  'ml-0.5',
].join(' '));

// Event handlers
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value: string | number = target.value;

  // Convert to number if type is number
  if (props.type === 'number') {
    value = target.valueAsNumber || target.value;
  }

  emit('update:modelValue', value);
  emit('change');
};

const handleFocus = () => {
  emit('focus');
};

const handleBlur = () => {
  isTouched.value = true;
  emit('blur');
};

// ARIA attributes
const ariaDescribedBy = computed(() => {
  const describedBy: string[] = [];

  if (hasError.value) {
    describedBy.push(`${inputId.value}-error`);
  }

  if (!hasError.value && props.helperText) {
    describedBy.push(`${inputId.value}-helper`);
  }

  return describedBy.length > 0 ? describedBy.join(' ') : undefined;
});
</script>

<template>
  <div class="space-y-1">
    <!-- Label -->
    <label v-if="label" :for="inputId" :class="labelClass">
      {{ label }}
      <span v-if="required" :class="requiredIndicatorClass">*</span>
    </label>

    <!-- Input wrapper -->
    <div :class="wrapperClass">
      <!-- Icon (left) -->
      <BaseIcon
        v-if="icon && iconPosition === 'left'"
        :icon="icon"
        size="lg"
        class="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
        aria-hidden="true"
      />

      <!-- Main input -->
      <input
        :id="inputId"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :autocomplete="autocomplete"
        :inputmode="inputmode"
        :min="min"
        :max="max"
        :step="step"
        :class="inputClass"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        :aria-invalid="hasError"
        :aria-describedby="ariaDescribedBy"
        :aria-required="required"
        :aria-label="ariaLabel"
      />

      <!-- Icon (right) -->
      <BaseIcon
        v-if="icon && iconPosition === 'right'"
        :icon="icon"
        size="lg"
        class="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
        aria-hidden="true"
      />
    </div>

    <!-- Error message -->
    <p v-if="hasError" :id="`${inputId}-error`" :class="errorClass" role="alert">
      <BaseIcon :icon="AlertCircle" size="sm" aria-hidden="true" />
      {{ error }}
    </p>

    <!-- Helper text -->
    <p v-else-if="helperText" :id="`${inputId}-helper`" :class="helperClass">
      {{ helperText }}
    </p>
  </div>
</template>

<style scoped>
.animate-shake-once {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}

/* Dark mode adjustments */
.dark .animate-shake-once {
  animation: shake-dark 0.5s ease-in-out;
}

@keyframes shake-dark {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}
</style>
